/**
 * CoffeeMates Partner Service
 */
import httpClient from '../utils/httpClient';

const partnerService = {
  async getProfile() { return (await httpClient.get('/partner/profile')).data; },
  async updateProfile(data: Record<string, unknown>) { return (await httpClient.put('/partner/profile', data)).data; },
  async getEvents() { return (await httpClient.get('/partner/events')).data; },
  async acknowledgeEvent(eventId: string) { return (await httpClient.post(`/partner/events/${eventId}/acknowledge`)).data; },
  async scanQR(qrToken: string) { return (await httpClient.post('/partner/scan', { qr_token: qrToken })).data; },
  async manualLookup(data: { short_code?: string; user_name?: string; event_id?: string }) { return (await httpClient.post('/partner/scan/manual', data)).data; },
  async getCheckins(eventId: string) { return (await httpClient.get(`/partner/events/${eventId}/checkins`)).data; },
  async getSettlements() { return (await httpClient.get('/partner/settlements')).data; },
  async getAnalytics() { return (await httpClient.get('/partner/analytics')).data; },
  async getDetailedAnalytics() { return (await httpClient.get('/partner/analytics/detailed')).data; },
  async getStaff() { return (await httpClient.get('/partner/staff')).data; },
  async addStaff(name: string, email?: string) { return (await httpClient.post('/partner/staff', { name, email: email || undefined })).data; },
  async removeStaff(staffId: string) { return (await httpClient.del(`/partner/staff/${staffId}`)).data; },
  async setAvailability(data: { available_days?: string[]; blackout_dates?: string[] }) { return (await httpClient.put('/partner/availability', data)).data; },
  async emergencyClosure() { return (await httpClient.post('/partner/emergency-closure')).data; },
  // Page view analytics
  async getPageViews(venueId: string, days?: number) { return (await httpClient.get(`/venues/analytics/page-views/${encodeURIComponent(venueId)}${days ? '?days=' + days : ''}`)).data; },
  // AI Insights
  async getAIInsights() { return (await httpClient.get('/partner/analytics/ai-insights')).data; },
  async clearAIInsightsCache() { return (await httpClient.del('/partner/analytics/ai-insights/cache')).data; },
  // Partner application
  async applyAsPartner(data: Record<string, unknown>) { return (await httpClient.post('/partner/apply', data)).data; },
  async getApplicationStatus() { return (await httpClient.get('/partner/application-status')).data; },
  // Automations
  async getAutomations() { return (await httpClient.get('/partner/automations')).data; },
  async toggleAutomation(type: string) { return (await httpClient.post(`/partner/automations/${type}/toggle`)).data; },
  // Idempotent on no-op; audit-logged on change.
  // Response: { microsite_template, branding_tier, updated_at }.
  async updateMicrositeTemplate(template: 'editorial' | 'action_first') {
    return (await httpClient.patch('/partner/microsite', { microsite_template: template })).data;
  },
  // Custom-domain wizard wrappers (claim is tier-gated). The wizard polls
  // listCustomDomains() every 10s while ssl_status='requested' to surface
  // ACM CNAMEs, flipping to step 4 when ssl_status='issued'.
  async claimCustomDomain(hostname: string) {
    return (await httpClient.post('/partner/website/custom-domain', { hostname })).data;
  },
  async listCustomDomains() {
    return (await httpClient.get('/partner/website/custom-domain')).data;
  },
  async verifyCustomDomain(domainId: string) {
    return (await httpClient.post(`/partner/website/custom-domain/${encodeURIComponent(domainId)}/verify`)).data;
  },
  async deleteCustomDomain(domainId: string) {
    return (await httpClient.del(`/partner/website/custom-domain/${encodeURIComponent(domainId)}`)).data;
  },
  // Loyalty customer list (GET /partner/loyalty/customers) — paginated
  // {customers, total, page, per_page, pages}; sort: last_visit|stamps|name.
  async getLoyaltyCustomers(params?: { page?: number; per_page?: number; sort?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.per_page) query.set('per_page', String(params.per_page));
    if (params?.sort) query.set('sort', params.sort);
    const qs = query.toString();
    return (await httpClient.get(`/partner/loyalty/customers${qs ? '?' + qs : ''}`)).data;
  },
  // Funnel aggregator; optional ?granularity=daily series feeds sparklines.
  async getMicrositeFunnel(venueId: string, range = 30, granularity?: 'daily' | 'weekly') {
    const params = new URLSearchParams({ range: String(range) });
    if (granularity) params.set('granularity', granularity);
    return (await httpClient.get(`/partner/venues/${encodeURIComponent(venueId)}/funnel?${params.toString()}`)).data;
  },
};

export default partnerService;
